/**
 * Local state for a device inspection: the draft answers and their update
 * helpers. Pure client state — saving is `useInspectionAutosave`'s job.
 *
 * Seeded once from the loaded inspection. A refetch after a save does not
 * re-seed it, so the operator's in-progress answers are never overwritten by
 * the echo of an earlier save.
 */

import { useCallback, useMemo, useState } from 'react';
import type {
  CheckInDeviceType,
  CheckInInspection,
  CheckInInspectionCheck,
} from '@techgitter/contracts';

import {
  inspectionToDraft,
  type InspectionDraftState,
} from '../utils/inspectionSchema';

export interface UseInspectionDraftResult {
  draft: InspectionDraftState;
  setField: <K extends keyof InspectionDraftState>(key: K, value: InspectionDraftState[K]) => void;
  setDamageCheck: (key: string, check: CheckInInspectionCheck) => void;
  setItemStatusCheck: (key: string, check: CheckInInspectionCheck) => void;
  markAllChecksNo: (group: 'damage' | 'itemStatus', rowKeys: string[]) => void;
}

export function useInspectionDraft(
  deviceType: CheckInDeviceType,
  initial: CheckInInspection | null | undefined,
): UseInspectionDraftResult {
  const [draft, setDraft] = useState<InspectionDraftState>(() => inspectionToDraft(deviceType, initial));

  const setField = useCallback<UseInspectionDraftResult['setField']>((key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  }, []);

  const setDamageCheck = useCallback((key: string, check: CheckInInspectionCheck) => {
    setDraft(prev => ({ ...prev, damageChecks: { ...prev.damageChecks, [key]: check } }));
  }, []);

  const setItemStatusCheck = useCallback((key: string, check: CheckInInspectionCheck) => {
    setDraft(prev => ({ ...prev, itemStatusChecks: { ...prev.itemStatusChecks, [key]: check } }));
  }, []);

  const markAllChecksNo = useCallback((group: 'damage' | 'itemStatus', rowKeys: string[]) => {
    setDraft(prev => {
      const target = group === 'damage' ? prev.damageChecks : prev.itemStatusChecks;
      const next: Record<string, CheckInInspectionCheck> = { ...target };
      for (const k of rowKeys) {
        if (!next[k] || next[k].status === null) {
          next[k] = { status: 'no', notes: next[k]?.notes ?? null };
        }
      }
      return group === 'damage'
        ? { ...prev, damageChecks: next }
        : { ...prev, itemStatusChecks: next };
    });
  }, []);

  return useMemo(
    () => ({ draft, setField, setDamageCheck, setItemStatusCheck, markAllChecksNo }),
    [draft, setField, setDamageCheck, setItemStatusCheck, markAllChecksNo],
  );
}

// Re-export for convenience
export { emptyDraft } from '../utils/inspectionSchema';
